const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/** Make an aria-modal behave modally for keyboard and assistive-technology users. */
export function activateModal(
  overlay: HTMLElement,
  panel: HTMLElement,
  options: { onEscape?: () => void; initialFocus?: HTMLElement } = {},
): () => void {
  const previousFocus =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const siblings = [...document.body.children].filter(
    (child): child is HTMLElement => child instanceof HTMLElement && child !== overlay,
  );
  const previousInert = siblings.map((element) => element.inert);
  for (const element of siblings) element.inert = true;

  const keydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && options.onEscape) {
      event.preventDefault();
      options.onEscape();
      return;
    }
    if (event.key !== 'Tab') return;
    const controls = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (element) => !element.hidden,
    );
    if (!controls.length) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = controls[0];
    const last = controls.at(-1)!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  overlay.addEventListener('keydown', keydown);
  window.setTimeout(() =>
    (options.initialFocus ?? panel.querySelector<HTMLElement>(FOCUSABLE))?.focus(),
  );

  return () => {
    overlay.removeEventListener('keydown', keydown);
    siblings.forEach((element, index) => (element.inert = previousInert[index]));
    previousFocus?.focus();
  };
}
